import {
  Column,
  Container,
  DeviationChat,
  DeviationDetails,
  ElasticContentContainer,
  EmptyScreen
} from '@cdab/scania/qpr/components'
import {
  useCssVariableBreakpoint,
  useGetDeviationChatMessages
} from '@cdab/scania/qpr/hooks'
import { deviationsController } from '@cdab/scania/qpr/offline/controllers'
import type {
  AuditPoint,
  ChatMessage,
  Deviation,
  DeviationValidationSchemaOut,
  FileData
} from '@cdab/scania/qpr/schema'
import { capitalizeFirstLetter, useDoOnce } from '@cdab/utils'
import {
  TdsButton,
  TdsFolderTab,
  TdsFolderTabs,
  TdsIcon,
  TdsSpinner,
  TdsToast
} from '@scania/tegel-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useLocation,
  useNavigate,
  useParams,
  useRouteLoaderData
} from 'react-router'
import { toast } from 'react-toastify'
import invariant from 'tiny-invariant'
import type { AuditLoaderData } from '../../../auditId'

import { useDeviation } from '@cdab/scania/qpr/contexts/deviation'
import { observer } from 'mobx-react-lite'
import type { FieldValues } from 'react-hook-form'
import {
  ActionRow,
  ActionRowMobile,
  ActionRowTitle,
  EmptyScreenWrapper,
  Left,
  Right,
  StyledRow,
  TabsContent
} from './edit-deviation.styles'

type Params = {
  auditId: string
  auditPointId: string
  deviationId: string
}

interface AuditDeviaionPageEditProps {
  oneColumnDetails: boolean
}

export const AuditDeviaionPageEdit = observer(
  ({ oneColumnDetails }: AuditDeviaionPageEditProps): JSX.Element => {
    const { t } = useTranslation()
    const isXxlg = useCssVariableBreakpoint('--tds-grid-xxlg')
    const { audit } = useRouteLoaderData('audit') as AuditLoaderData
    const { deviations, isReadonly } = audit
    const params = useParams<Params>()
    const deviationId = params.deviationId
    const navigate = useNavigate()
    const { pathname, state } = useLocation()
    const [isEditing, setIsEditing] = useState(false)
    const ctx = useDeviation()

    const [selectedTabIndex, setSelectedTabIndex] = useState(0)
    const [uploadedDealerFilesState, setUploadedDealerFilesState] = useState<
      FileData[]
    >([])

    const [chatMessages, setChatMessages] = useState<ChatMessage[] | undefined>(
      undefined
    )

    const deviation = deviations.find(
      deviation => deviation.clientGuid === deviationId
    )
    invariant(deviation, 'Deviation not found')

    const { auditPointId: sAuditPointId } = params
    const auditPointId = Number.parseInt(sAuditPointId ?? 'NaN')
    let auditPoint: AuditPoint | undefined = undefined
    if (!Number.isNaN(auditPointId)) {
      auditPoint = audit.auditPoints.find(ap => ap.id === auditPointId)
      invariant(
        auditPoint,
        `Could not find audit point with id ${auditPointId}`
      )
    }

    const [getDeviationChatMessagesState, getDeviationChatMessages] =
      useGetDeviationChatMessages()

    useEffect(() => {
      if (getDeviationChatMessagesState.status === 'success') {
        setChatMessages(getDeviationChatMessagesState.chatMessages)
      }
    }, [getDeviationChatMessagesState, chatMessages])

    useDoOnce(() => {
      if (!deviation.id) return
      getDeviationChatMessages(deviation.id)
    })

    useEffect(() => {
      ctx.updateDeviation(deviation, auditPoint)
    }, [deviationId, auditPoint])

    const updateDeviation = (
      data: DeviationValidationSchemaOut
    ): Promise<void> => {
      const common: Partial<Deviation> = {
        ...deviation,
        auditPointId: data.auditPointId,
        auditPointNumber: data.auditPointNumber,
        deviation: data.deviation
      }

      const updatedDeviation: Deviation = data.withActionPlan
        ? {
            ...(common as Required<typeof common>),
            deviationWithoutActions: false,
            approvalDate: data.completed ?? null,
            expirationDate: data.due,
            proposedActions: data.action,
            responsible: data.assignee
          }
        : {
            ...(common as Required<typeof common>),
            deviationWithoutActions: true,
            approvalDate: null,
            expirationDate: null,
            proposedActions: null,
            responsible: null
          }
      deviationsController.UpdateDeviation(updatedDeviation)

      if (data.files) {
        deviationsController.AddFiles(
          audit.id,
          deviation.clientGuid,
          data.files.map(({ file }) => file)
        )
      }

      data.filesToDelete?.forEach(fileId => {
        deviationsController.DeleteFile(audit.id, deviation.clientGuid, fileId)
      })
      ctx?.form?.setValue('filesToDelete', [])
      return Promise.resolve()
    }

    async function saveDeviation(
      deviation: DeviationValidationSchemaOut | FieldValues
    ) {
      try {
        await updateDeviation(deviation as DeviationValidationSchemaOut)

        toast(
          <TdsToast
            variant='success'
            header={capitalizeFirstLetter(t('deviation-saved'))}
            subheader={capitalizeFirstLetter(t('deviation-updated'))}
          />
        )
        setIsEditing(false)
      } catch (error) {
        toast(
          <TdsToast
            variant='error'
            header={capitalizeFirstLetter(t('deviation-not-saved'))}
            subheader={capitalizeFirstLetter(t('deviation-not-saved-error'))}
          />
        )
      }
    }

    const onDelete = () => {
      if (window.confirm('Are you sure you want to delete this deviation?')) {
        deviationsController.DeleteDeviation(audit.id, deviation.clientGuid)

        goToPrevoiusPage()
        toast(
          <TdsToast
            variant='success'
            header={capitalizeFirstLetter(t('deviation-deleted'))}
            subheader={capitalizeFirstLetter(t('deviation-deleted-success'))}
          />
        )
      }
    }

    const onUploadFile = (file: FileData) => {
      setUploadedDealerFilesState([...uploadedDealerFilesState, file])
    }

    useEffect(() => {
      deviationsController.GetFiles(audit.id, deviation.clientGuid)
    }, [audit.id, deviation?.clientGuid])

    //TODO: should we show only files from dealer messages?
    const dealerFiles =
      getDeviationChatMessagesState.status === 'success'
        ? getDeviationChatMessagesState.chatFiles.concat(
            uploadedDealerFilesState
          )
        : []

    const goToPrevoiusPage = () => {
      sessionStorage.setItem('previousDeviationGuid', deviation.clientGuid)
      const lastIndexPath = pathname.lastIndexOf('/')
      let previousPath: string | undefined = undefined
      if (lastIndexPath !== -1) {
        previousPath = pathname.substring(0, lastIndexPath)
      }

      if (state && 'previousPath' in state) {
        navigate(state.previousPath)
      } else if (previousPath) {
        navigate(previousPath)
      }
    }

    function ActionButtons() {
      return (
        <ActionRow>
          <Left>
            {!isReadonly && isEditing ? (
              <TdsButton
                variant='danger'
                onClick={onDelete}
                text={t('delete')}
                size='sm'
              />
            ) : (
              <TdsButton
                onClick={goToPrevoiusPage}
                size='sm'
                text={capitalizeFirstLetter(t('back'))}
                variant='secondary'
              />
            )}
          </Left>

          <ActionRowTitle>
            {isEditing ? 'Editing ' : t('deviation-details', { ns: 'audit' })}
          </ActionRowTitle>

          <Right>
            {!isReadonly && isEditing && (
              <>
                <TdsButton
                  variant='secondary'
                  text={capitalizeFirstLetter(t('cancel'))}
                  size='sm'
                  onClick={() => setIsEditing(false)}
                  className='tds-u-mr2'
                />
                <TdsButton
                  size='sm'
                  onClick={() => {
                    ctx?.form?.handleSubmit(saveDeviation)()
                  }}
                  text={capitalizeFirstLetter(t('save'))}
                />
              </>
            )}

            {!isReadonly && !isEditing && (
              <TdsButton
                size='sm'
                text={capitalizeFirstLetter(t('edit'))}
                onClick={() => setIsEditing(true)}
              />
            )}
          </Right>
        </ActionRow>
      )
    }

    function ActionButtonsMobile() {
      return (
        <ActionRowMobile>
          <Left>
            {!isReadonly && isEditing ? (
              <TdsButton
                variant='danger'
                onClick={onDelete}
                text={t('delete')}
                size='sm'
              />
            ) : (
              <TdsButton
                onClick={goToPrevoiusPage}
                size='sm'
                text={capitalizeFirstLetter(t('back'))}
                variant='secondary'
              />
            )}
          </Left>

          <ActionRowTitle />

          <Right>
            {!isReadonly && isEditing && (
              <>
                <TdsButton
                  variant='secondary'
                  text={capitalizeFirstLetter(t('cancel'))}
                  size='sm'
                  onClick={() => setIsEditing(false)}
                  className='tds-u-mr2'
                />
                <TdsButton
                  size='sm'
                  onClick={() => {
                    ctx?.form?.handleSubmit(saveDeviation)()
                  }}
                  text={capitalizeFirstLetter(t('save'))}
                />
              </>
            )}

            {!isReadonly && !isEditing && (
              <TdsButton
                size='sm'
                text={capitalizeFirstLetter(t('edit'))}
                onClick={() => setIsEditing(true)}
              />
            )}
          </Right>
        </ActionRowMobile>
      )
    }

    if (isXxlg) {
      return (
        <Container
          fluid='normal'
          paddingOnContainer={false}
          fullHeight
          scrollY={false}
        >
          <StyledRow>
            <Column
              width={6}
              style={{
                borderRight: '1px solid var(--tds-divider-background-discrete)'
              }}
              padding={false}
            >
              <ElasticContentContainer
                scrollY
                overflowHidden
                header={<ActionButtons />}
              >
                <div className='tds-u-p2'>
                  <DeviationDetails
                    editMode={isEditing}
                    deviation={deviation}
                    isReadOnly={isReadonly}
                    auditId={audit.id}
                    onDelete={onDelete}
                    chatFiles={dealerFiles}
                    defaultAuditPoint={auditPoint}
                    oneColumn={oneColumnDetails}
                  />
                </div>
              </ElasticContentContainer>
            </Column>
            <Column width={6} padding={false}>
              {getDeviationChatMessagesState.status === 'success' &&
              chatMessages ? (
                <DeviationChat
                  messages={chatMessages}
                  actionPlanItemId={deviation.id}
                  files={getDeviationChatMessagesState.chatFiles}
                  onUploadFile={onUploadFile}
                  isReadOnly={isReadonly}
                  onMessagesChange={(messages: ChatMessage[]) => {
                    setChatMessages(messages)
                  }}
                />
              ) : getDeviationChatMessagesState.status === 'loading' ? (
                <TdsSpinner />
              ) : (
                getDeviationChatMessagesState.status === 'error' && (
                  <EmptyScreenWrapper>
                    <EmptyScreen
                      title={t('something-went-wrong', { ns: 'common' })}
                      description={t('chat-is-not-loaded', { ns: 'common' })}
                      icon={<TdsIcon name='warning' size='lg' />}
                    />
                  </EmptyScreenWrapper>
                )
              )}
            </Column>
          </StyledRow>
        </Container>
      )
    }

    return (
      <Container paddingOnContainer={false} fullHeight scrollY={false}>
        <StyledRow>
          <Column width={12} padding={false}>
            <TdsFolderTabs
              modeVariant='primary'
              onTdsChange={e => setSelectedTabIndex(e.detail.selectedTabIndex)}
            >
              <TdsFolderTab>
                <button>
                  {capitalizeFirstLetter(t('deviation', { ns: 'common' }))}
                </button>
              </TdsFolderTab>
              <TdsFolderTab>
                <button>
                  {capitalizeFirstLetter(t('chat', { ns: 'common' }))}
                </button>
              </TdsFolderTab>
            </TdsFolderTabs>
            <TabsContent>
              {selectedTabIndex === 0 ? (
                <ElasticContentContainer
                  scrollY
                  overflowHidden
                  header={<ActionButtonsMobile />}
                >
                  <div style={{ padding: '16px' }}>
                    <DeviationDetails
                      editMode={isEditing}
                      deviation={deviation}
                      isReadOnly={isReadonly}
                      auditId={audit.id}
                      onDelete={onDelete}
                      chatFiles={dealerFiles}
                      defaultAuditPoint={auditPoint}
                      oneColumn={oneColumnDetails}
                    />
                  </div>
                </ElasticContentContainer>
              ) : getDeviationChatMessagesState.status === 'success' &&
                chatMessages ? (
                <DeviationChat
                  messages={chatMessages}
                  actionPlanItemId={deviation.id}
                  files={getDeviationChatMessagesState.chatFiles}
                  onUploadFile={onUploadFile}
                  isReadOnly={isReadonly}
                  onMessagesChange={(messages: ChatMessage[]) => {
                    setChatMessages(messages)
                  }}
                />
              ) : getDeviationChatMessagesState.status === 'loading' ? (
                <TdsSpinner />
              ) : (
                getDeviationChatMessagesState.status === 'error' && (
                  <EmptyScreenWrapper>
                    <EmptyScreen
                      title={t('something-went-wrong', { ns: 'common' })}
                      description={t('chat-is-not-loaded', { ns: 'common' })}
                      icon={<TdsIcon name='warning' size='lg' />}
                    />
                  </EmptyScreenWrapper>
                )
              )}
            </TabsContent>
          </Column>
        </StyledRow>
      </Container>
    )
  }
)
